import React, { useEffect } from 'react';
import styles from './Members.module.scss';
import { WarningRounded } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { ModalComponent } from 'new/routes/RouterManager';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { useDispatch } from 'react-redux';
import { activateUser, deactivateUser } from 'js/actions';
import { ACTIVATE_USER_REQUEST, DEACTIVATE_USER_REQUEST } from 'js/constants/actionTypes';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { showAlert } from 'new/stores/showAlert/action';
import { useModals } from 'new/hooks/useModals';
import { ProfileGroup } from 'new/elements/ProfileGroup/ProfileGroup';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

interface DeactivateUserProps {
  deactivate: boolean;
  members: { full_name: string; email: string; id: number }[];
}

function getNames(members: any[]) {
  const reduced = members.reduce((acc, cur, idx) => {
    if (idx < 5) {
      acc.push(cur.full_name);
    }
    return acc;
  }, []);
  return (
    <span>
      {reduced.map((r, i) => (
        <b key={r}>
          {r}
          {i < reduced.length - 1 ? ', ' : ' '}
        </b>
      ))}
      {members.length > 3
        ? `and ${members.length - 3} ${members.length > 1 ? 'other' : 'others'}`
        : ''}{' '}
      {reduced.length > 1 ? 'accounts' : 'account'}
    </span>
  );
}

export const DeactivateUsers: ModalComponent = (props: DeactivateUserProps & ModalOutProps) => {
  const { members, deactivate, setModalActions, setLoading, setTitle } = props;
  const loading = useIsLoading(DEACTIVATE_USER_REQUEST, ACTIVATE_USER_REQUEST);

  const { closeModal } = useModals();

  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

  const actionSuccess = () => {
    closeModal(DeactivateUsers.modalConfig.name);
    dispatch(
      showAlert({
        title: members.length + ' users ' + (deactivate ? ' deactivated.' : 'activated.'),
        severity: 'success',
      }),
    );
  };

  const onConfirm = () => {
    let action;
    if (deactivate) {
      action = dispatch(deactivateUser(members.map((m) => ({ email: m.email }))));
    } else {
      action = dispatch(activateUser(members.map((m) => ({ email: m.email }))));
    }
    action.then(() => {
      actionSuccess();
    });
  };

  useDidUpdateEffect(() => {
    setLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!deactivate) {
      setTitle('Activate user');
    }
    setModalActions({
      ok: {
        text: deactivate ? 'Deactivate' : 'Activate',
        color: deactivate ? 'error' : 'primary',
        onClick: onConfirm,
      },
    });
  }, []);

  return (
    <div className={styles.deactivateContainer}>
      <div>
        <div className={styles.imageContainer}>
          <ProfileGroup items={members.map((i) => ({ ...i, fullName: i.full_name }))} />
          <div className={styles.badge}>
            <WarningRounded />
          </div>
        </div>
      </div>
      <Typography className={styles.deactivateText} textAlign="center" variant="body1">
        You are about to {deactivate ? 'deactivate' : 'activate'} {getNames(members)}. Are you sure
        you want to continue?
      </Typography>
    </div>
  );
};

DeactivateUsers.modalConfig = {
  name: 'members.DeactivateUser',
  title: 'Deactivate user',
  maxWidth: 'md',
};
